// See https://tfhub.dev/google/spice/2
import * as tf from '@tensorflow/tfjs';

const NUM_INPUT_SAMPLES = 1024;
const MODEL_SAMPLE_RATE = 16000;
const PT_OFFSET = 25.58;
const PT_SLOPE = 63.07;
const MODEL_URL = 'https://tfhub.dev/google/tfjs-model/spice/2/default/1';

type SpicePitchState = 'uninitialized' | 'loading' | 'stopped' | 'running' | 'errored';

type SpicePitchDetectedCallback = (data: SpiceEstimate[]) => void;

interface SpiceEstimate {
  frequency: number;
  confidence: number;
}

export class SpicePitch {
  constructor(private model_url: string = MODEL_URL) {
    this.loadingPromise = this.loadModel();
  }

  private loadingPromise: Promise<void>;

  model: tf.GraphModel | undefined;

  private stream: MediaStream | undefined = undefined;

  private processor: ScriptProcessorNode | undefined;

  private modelLoadingPromise: Promise<tf.GraphModel> | undefined;

  private pitchDetectedCallbacks: SpicePitchDetectedCallback[] = [];

  private stateChangedCallbacks: ((state: SpicePitchState) => void)[] = [];

  public async loadModel() {
    console.log('loading spice model...');
    if (typeof window === 'undefined') return;
    if (this.model) return;
    // await tf.setBackend('wasm');

    if (this._state === 'loading' && this.modelLoadingPromise) {
      await this.modelLoadingPromise;
    }

    this.setState('loading');
    this.modelLoadingPromise = tf
      .loadGraphModel(this.model_url, { fromTFHub: this.model_url.includes('tfhub.dev') })
      .then((model) => {
        this.model = model;
        this.setState('stopped');
        console.log('spice model loaded');
        return model;
      })
      .catch((err) => {
        console.log('error loading spice model');
        this.setState('errored');
        throw err;
      });

    await this.modelLoadingPromise;
  }

  private _state: SpicePitchState = 'uninitialized';

  addPitchDetectedCallback(callback: SpicePitchDetectedCallback) {
    this.pitchDetectedCallbacks.push(callback);
  }

  addStateChangedCallback(callback: (state: SpicePitchState) => void) {
    this.stateChangedCallbacks.push(callback);
  }

  removePitchDetectedCallback(callback: SpicePitchDetectedCallback) {
    this.pitchDetectedCallbacks = this.pitchDetectedCallbacks.filter((cb) => cb !== callback);
  }

  private setState(state: SpicePitchState) {
    this._state = state;
    this.stateChangedCallbacks.forEach((cb) => cb(state));
  }

  get state() {
    return this._state;
  }

  async start() {
    if (typeof window === 'undefined') return;
    if (this.state === 'running') return;
    if (!this.model) {
      if (!this.loadingPromise) {
        await this.loadModel();
      } else {
        await this.loadingPromise;
      }
    }

    if (!navigator?.mediaDevices?.getUserMedia) {
      this.setState('errored');
      // No audio allowed
      throw Error('Browser is not supported, getUserMedia is required.');
    }

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        latencyHint: 'playback',
        sampleRate: MODEL_SAMPLE_RATE,
      });

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      // returns a promise that resolves on first pitch detection.
      await this.handleSuccess({
        stream,
        audioContext,
      });
    } catch (err) {
      console.error(err);
      this.setState('errored');
      throw Error(
        'Sorry, microphone permissions are required for the app. Feel free to read on without playing :)'
      );
    }
  }

  stop() {
    this.processor?.disconnect();
    this.stream?.getTracks().forEach((track) => {
      track.stop();
    });

    this.signalChain.forEach((node) => node.disconnect());
    this.signalChain = [];

    this.stream = undefined;
    this.setState('stopped');
  }

  private signalChain: { disconnect: () => void }[] = [];

  private handleSuccess({
    stream,
    audioContext,
  }: {
    stream: MediaStream;
    audioContext: AudioContext;
  }) {
    if (!this.model) {
      throw Error('Cannot handle user media before tensorflow model is loaded.');
    }
    const model = this.model;
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(
      NUM_INPUT_SAMPLES,
      /* num_inp_channels= */ 1,
      /* num_out_channels= */ 1
    );

    this.signalChain.push(source, processor, audioContext.destination);

    // Converts audio to mono.
    processor.channelInterpretation = 'speakers';
    processor.channelCount = 1;

    // Runs processor on audio source.
    source.connect(processor);
    processor.connect(audioContext.destination);

    let awaitingStart = true;

    let firstPitchDetectedResolver: () => void;
    const firstPitchDetectedPromise = new Promise<void>((resolve) => {
      firstPitchDetectedResolver = resolve;
    });

    const notify = (estimates: SpiceEstimate[]) => {
      if (awaitingStart) {
        firstPitchDetectedResolver();
        awaitingStart = false;
      }
      this.notify(estimates);
    };

    let inputCount = 0;
    processor.onaudioprocess = function handleAudio(e: AudioProcessingEvent) {
      // Take only each 4th sample to save processing...
      inputCount = (inputCount + 1) % 4;
      if (inputCount !== 0) return;

      const inputData = e.inputBuffer.getChannelData(0);
      const input = tf.reshape(tf.tensor(inputData), [NUM_INPUT_SAMPLES]);
      const output = model.execute({ input_audio_samples: input }) as tf.Tensor<tf.Rank>[];
      const uncertainties = output[0]?.dataSync();
      const pitches = output[1]?.dataSync();

      if (!pitches || !uncertainties) return;
      const pitchArray = Array.from(pitches);
      const uncertaintiesArray = Array.from(uncertainties);
      const estimates = pitchArray.reduce((priorEstimates: SpiceEstimate[], tfPitchNumber, i) => {
        const uncertainty = uncertaintiesArray[i];
        priorEstimates.push({
          frequency: getPitchHz(tfPitchNumber),
          confidence: 1 - (typeof uncertainty === 'number' ? uncertainty : 1),
        });
        return priorEstimates;
      }, []);

      notify(estimates);
    };
    this.processor = processor;
    this.stream = stream;
    return firstPitchDetectedPromise;
  }

  private notify(estimates: SpiceEstimate[]) {
    this.pitchDetectedCallbacks.forEach((cb) => cb(estimates));
  }
}

function getPitchHz(modelPitch: number) {
  const fmin = 10.0;
  const binsPerOctave = 12.0;
  const cqtBin = modelPitch * PT_SLOPE + PT_OFFSET;
  return fmin * 2.0 ** ((1.0 * cqtBin) / binsPerOctave);
}
